import React, { useLayoutEffect, useRef } from 'react'
import AutoScroll from '@/components/AutoScroll/AutoScroll'
import { lightTheme } from '@/customTheme'
import { hexToRgba } from '@/scenes/engine/utils/utils'
import { Z_INDEX } from '@/constants/z-index'
import PopoverPlacement from '../PopoverPlacement/PopoverPlacement'
import PopoverHeader from './PopoverHeader'
import CategoryHorizontalScroller, { CategoryItem } from '../CategoryHorizontalScroller'
interface ToolPopoverProps {
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
  margin?: number
  width?: PopoverWidthType
  minWidth?: number
  maxWidth?: number
  height?: number
  maxHeight?: number
  padding?: PopoverPaddingType
  borderRadius?: PopoverBorderRadius
  gap?: PopoverGapType
  children?: React.ReactNode
  closeOnSelection?: boolean
  /** Called to close the popover after a click inside it, when `closeOnSelection` is true. */
  closePopover?: () => void
  buttonRef?: React.RefObject<HTMLElement>
  relativeToRef?: React.RefObject<HTMLElement>
  title?: string
  categories?: CategoryItem[]
  onCategoryClick?: (categoryId: string) => void
  showCloseButton?: boolean
  /** Called when close button is clicked; required for showCloseButton to work */
  onClose?: () => void
  /** When true, scrolls the scroll container to the bottom on mount */
  scrollToBottom?: boolean
  /** Increment/change this value to scroll the content to the bottom after render */
  scrollToBottomTrigger?: number
}

export type PopoverWidthType = 'auto' | 70 | 76 | 112 | 224 | 235 | 250 | 262 | 276 | 292 | 320 | 348
export type PopoverPaddingType = 0 | 4 | 10 | 16 | 20 | 24 | '20,16' | '8,4' | '8,16' | '16,0,0,16' | '12,16,16,16'
export type PopoverBorderRadius = 10 | 12 | 16
export type PopoverGapType = 0 | 4 | 8 | 16 | 24

const PADDING_WHEN_SCROLL_ENABLED = '16px 0 0 16px'

function getPaddingValue(padding: PopoverPaddingType) {
  if (typeof padding === 'number') {
    return `${padding}px`
  }
  // padding is a string like '20,10' or '16,0,0,16'
  return padding
    .split(',')
    .map((value) => `${Number(value)}px`)
    .join(' ')
}

function WrapPopover({
  placement = 'bottom',
  margin = 8,
  width = 262,
  minWidth,
  maxWidth,
  height,
  maxHeight,
  padding = 16,
  borderRadius = 16,
  gap = 16,
  children,
  closeOnSelection = false,
  closePopover,
  buttonRef,
  relativeToRef,
  title,
  categories,
  onCategoryClick,
  showCloseButton,
  onClose,
  scrollToBottom,
  scrollToBottomTrigger,
}: ToolPopoverProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const hasHeader = Boolean(title)
  const hasCategories = Boolean(categories && categories.length > 0)
  const showClose = Boolean(showCloseButton && onClose)
  const scrollEnabled = Boolean(height || maxHeight)
  const wrapperScrollPaddingStyle: React.CSSProperties = scrollEnabled ? { paddingRight: '16px' } : {}

  useLayoutEffect(() => {
    if (!scrollRef.current) return

    if (scrollToBottomTrigger != null) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
      return
    }

    if (scrollToBottom) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [scrollToBottom, scrollToBottomTrigger])

  const handleCategoryClick = (categoryId: string) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
    onCategoryClick?.(categoryId)
  }

  const content = scrollEnabled ? (
    <AutoScroll
      ref={scrollRef}
      style={{
        flex: 1,
        minHeight: 0,
        padding: 0,
        paddingRight: '4px',
        paddingTop: 0,
        paddingLeft: 0,
        marginRight: '4px',
        height: '100%'
      }}
      bottomMarginToContent={16}
      scrollThumbMarginBottom={16}
    >
      {children}
    </AutoScroll>) : children

  return (
    <PopoverPlacement
      placement={placement}
      margin={margin}
      buttonRef={buttonRef}
      relativeToRef={relativeToRef}
      style={{
        zIndex: Z_INDEX.FLOATING_INSPECTOR_POPOVER,
        width: width,
        ...(minWidth != null && { minWidth }),
        ...(maxWidth != null && { maxWidth }),
        ...(height != null && { height }),
        ...(maxHeight != null && { maxHeight }),
        background: lightTheme.colors.white,
        borderRadius: `${borderRadius}px`,
        padding: scrollEnabled ? PADDING_WHEN_SCROLL_ENABLED : getPaddingValue(padding),
        display: 'flex',
        flexDirection: 'column',
        gap: `${gap}px`,
        boxShadow: `0px 4px 12px ${hexToRgba(lightTheme.colors.black, 0.12)}`,
      }}
      onClick={(e) => (closeOnSelection ? closePopover?.() : e.stopPropagation())}
      // Always stop mousedown, regardless of closeOnSelection: WithPopover's
      // outside-close listens on mousedown (not click), and the popover content
      // is portaled outside the button's DOM subtree, so an unstopped mousedown
      // on e.g. a menu item bubbles to that listener and unmounts the popover
      // before the item's own click handler ever runs. Closing on selection is
      // instead done explicitly above, on click, via closePopover.
      onMouseDown={(e) => e.stopPropagation()}
    >
      {hasHeader &&
      <div style={wrapperScrollPaddingStyle}>
        <PopoverHeader title={title} showClose={showClose} onClose={onClose} />
      </div>}
      {hasCategories &&
        <div style={wrapperScrollPaddingStyle}>
          <div style={{ paddingTop: hasHeader ? '8px' : '0px', paddingBottom: '4px' }}>
            <CategoryHorizontalScroller categories={categories} initialActiveCategory={categories[0]?.id} onCategoryClick={handleCategoryClick} />
          </div>
        </div>
      }
      {content}
    </PopoverPlacement>
  )
}

export default WrapPopover
